import { z } from 'zod';
import { languageCodeSchema } from '../facility';

export const contentTemplateTransformerKeyValues = [
  'driver-tour',
  'guide-tour',
] as const;
export const contentTemplateTransformerKeySchema = z.enum(
  contentTemplateTransformerKeyValues
);
export type ContentTemplateTransformerKey = z.infer<
  typeof contentTemplateTransformerKeySchema
>;

export const contentTemplateItemSchema = z.object({
  id: z.uuid(),
  externalTemplateId: z.string(),
  title: z.string(),
  content: z.string().nullable(),
  transformerKey: contentTemplateTransformerKeySchema.nullable(),
  isMock: z.boolean(),
  // Language of the template's wording. Message templates point at a base;
  // other languages are versions of it (XT-758).
  languageCode: z.string().default('en'),
  baseContentTemplateId: z.uuid().nullable().default(null),
});
export type ContentTemplateItem = z.infer<typeof contentTemplateItemSchema>;

export const findContentTemplatesInput = z.object({
  offset: z.number().int().nonnegative().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  title: z.string().optional(),
});
export type FindContentTemplatesInput = z.infer<
  typeof findContentTemplatesInput
>;

export const findContentTemplatesResultSchema = z.object({
  data: z.array(contentTemplateItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindContentTemplatesResult = z.infer<
  typeof findContentTemplatesResultSchema
>;

// `default` = inherited from the default template organization (TempoVIP),
// used until the organization adds its own version for that language.
export const contentTemplateLanguageSourceValues = [
  'organization',
  'default',
] as const;
export type ContentTemplateLanguageSource =
  (typeof contentTemplateLanguageSourceValues)[number];

export const contentTemplateLanguageVersionSchema = z.object({
  id: z.uuid(),
  baseContentTemplateId: z.uuid(),
  languageCode: z.string(),
  externalTemplateId: z.string(),
  title: z.string(),
  content: z.string().nullable(),
  source: z.enum(contentTemplateLanguageSourceValues),
});
export type ContentTemplateLanguageVersion = z.infer<
  typeof contentTemplateLanguageVersionSchema
>;

export const contentTemplateWithLanguagesSchema =
  contentTemplateItemSchema.extend({
    languageVersions: z.array(contentTemplateLanguageVersionSchema),
  });
export type ContentTemplateWithLanguages = z.infer<
  typeof contentTemplateWithLanguagesSchema
>;

export const findContentTemplateLanguagesResultSchema = z.object({
  data: z.array(contentTemplateWithLanguagesSchema),
});
export type FindContentTemplateLanguagesResult = z.infer<
  typeof findContentTemplateLanguagesResultSchema
>;

export const createContentTemplateLanguageVersionInput = z.object({
  baseContentTemplateId: z.uuid(),
  languageCode: languageCodeSchema,
  externalTemplateId: z.string().trim().min(1, 'Content SID is required'),
  title: z.string().trim().min(1).optional(),
  content: z.string().nullable().optional(),
});
export type CreateContentTemplateLanguageVersionInput = z.infer<
  typeof createContentTemplateLanguageVersionInput
>;

export const updateContentTemplateLanguageVersionInput = z.object({
  id: z.uuid(),
  externalTemplateId: z
    .string()
    .trim()
    .min(1, 'Content SID is required')
    .optional(),
  title: z.string().trim().min(1).optional(),
  content: z.string().nullable().optional(),
});
export type UpdateContentTemplateLanguageVersionInput = z.infer<
  typeof updateContentTemplateLanguageVersionInput
>;

export const deleteContentTemplateLanguageVersionInput = z.object({
  id: z.uuid(),
});
export type DeleteContentTemplateLanguageVersionInput = z.infer<
  typeof deleteContentTemplateLanguageVersionInput
>;
